import React, { useEffect, useState } from "react";
import { ShieldCheck, KeyRound, Plus, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import {
    hydrateCreateOptions, serialiseAttestation, isWebAuthnSupported,
} from "../lib/webauthn";
import { useAuth } from "../context/AuthContext";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const daysUntil = (iso) => {
    if (!iso) return null;
    try {
        const diff = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
        return diff;
    } catch { return null; }
};

const SecurityKeysPanel = () => {
    const { user } = useAuth();
    const [creds, setCreds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [nickname, setNickname] = useState("");
    const [enrolling, setEnrolling] = useState(false);
    const isPriv = user && ["admin", "board"].includes(user.role);
    const graceDays = user?.role && isPriv ? daysUntil(user.webauthn_grace_until) : null;

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/webauthn/credentials");
            setCreds(data.credentials || []);
        } catch (e) {
            toast.error("Could not load security keys");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const enroll = async (e) => {
        e?.preventDefault?.();
        if (!isWebAuthnSupported()) {
            toast.error("Your browser doesn't support WebAuthn / security keys.");
            return;
        }
        setEnrolling(true);
        try {
            const { data: begin } = await api.post("/webauthn/register/begin", { nickname });
            const publicKey = hydrateCreateOptions(JSON.parse(begin.options));
            const cred = await navigator.credentials.create({ publicKey });
            if (!cred) throw new Error("Browser did not return a credential.");
            await api.post("/webauthn/register/complete", {
                challenge_token: begin.challenge_token,
                attestation: serialiseAttestation(cred),
            });
            toast.success("Security key enrolled.");
            setNickname("");
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.name === "NotAllowedError") {
                toast.error("The browser cancelled the security-key prompt. Try again.");
            } else {
                toast.error(typeof detail === "string" ? detail : (err?.message || "Enrollment failed"));
            }
        } finally {
            setEnrolling(false);
        }
    };

    const remove = async (id, nick) => {
        if (!window.confirm(`Remove "${nick}"? You'll need another enrolled key to sign in if this was your only one.`)) return;
        try {
            await api.delete(`/webauthn/credentials/${id}`);
            toast.success("Security key removed");
            await load();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    return (
        <div className="bg-white border border-black/5 rounded-2xl p-6 mt-6" data-testid="security-keys-panel">
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} /> Security keys (FIDO2 / passkey)
                </h2>
                {isPriv && (
                    <span data-testid="security-grace-pill" className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full bg-[#F4F1EA] text-[#1C252E]">
                        {creds.length > 0
                            ? <>✓ 2FA enabled</>
                            : graceDays !== null && graceDays > 0
                                ? <><AlertTriangle className="w-3.5 h-3.5 text-[#E53935]" strokeWidth={2} /> Enrollment required in {graceDays} day{graceDays === 1 ? "" : "s"}</>
                                : <><AlertTriangle className="w-3.5 h-3.5 text-[#E53935]" strokeWidth={2} /> Enrollment overdue</>}
                    </span>
                )}
            </div>
            <p className="text-sm text-[#4A5568] mb-5">
                Sign in with your password, then tap a hardware security key (e.g. <em>Thetis Pro PinPlex</em>) to complete login.
                Security keys are the only second factor — TOTP and SMS are not supported.
                {isPriv && creds.length === 0 && (
                    <span className="block mt-2 text-[#E53935] font-medium">
                        Admin/board accounts must enroll a security key within the 30-day grace period.
                    </span>
                )}
            </p>

            <form onSubmit={enroll} className="flex flex-col sm:flex-row gap-2 mb-6">
                <label htmlFor="key-nickname" className="sr-only">Nickname</label>
                <input
                    id="key-nickname"
                    type="text"
                    placeholder='Nickname (e.g. "Office Thetis")'
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    maxLength={80}
                    data-testid="security-key-nickname-input"
                    className="flex-1 rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                />
                <button
                    type="submit"
                    disabled={enrolling}
                    data-testid="security-key-enroll-btn"
                    className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-60 transition"
                >
                    <Plus className="w-4 h-4" strokeWidth={2} />
                    {enrolling ? "Tap your key…" : "Enroll a new key"}
                </button>
            </form>

            {loading ? (
                <p className="text-sm text-[#4A5568]">Loading…</p>
            ) : creds.length === 0 ? (
                <p className="text-sm text-[#4A5568] italic" data-testid="security-keys-empty">No security keys enrolled yet.</p>
            ) : (
                <ul className="space-y-2" data-testid="security-keys-list">
                    {creds.map((c) => (
                        <li key={c.id} data-testid={`security-key-row-${c.id}`} className="flex items-center justify-between gap-3 p-3 bg-[#F4F1EA]/50 rounded-xl">
                            <div className="flex items-center gap-3 min-w-0">
                                <KeyRound className="w-5 h-5 text-[#1C252E] flex-shrink-0" strokeWidth={1.5} />
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-[#1C252E] truncate">{c.nickname}</p>
                                    <p className="text-xs text-[#4A5568]">
                                        Added {fmtDate(c.created_at)}
                                        {c.last_used_at && <> · last used {fmtDate(c.last_used_at)}</>}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => remove(c.id, c.nickname)}
                                data-testid={`security-key-remove-${c.id}`}
                                aria-label={`Remove ${c.nickname}`}
                                className="p-2 rounded-full text-[#4A5568] hover:text-[#E53935] hover:bg-[#E53935]/10 transition"
                            >
                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default SecurityKeysPanel;
